import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { plainButton } from '../controls';

export type BulkAction = 'claim' | 'unclaim' | 'snooze' | 'dismiss';

const BUSY_LABEL: Record<BulkAction, string> = { claim: 'Claiming…', unclaim: 'Unclaiming…', snooze: 'Snoozing…', dismiss: 'Dismissing…' };

function Kbd({ children }: { children: React.ReactNode }) {
  return <Box component="kbd" aria-hidden="true" sx={{ font: 'inherit', fontSize: '0.65625rem', fontWeight: 600, lineHeight: 1, border: '1px solid #CBD5E1', borderRadius: '4px', px: '4px', py: '2px', color: '#64748B', bgcolor: '#FFFFFF' }}>{children}</Box>;
}

const barButton = {
  ...plainButton,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  border: 0,
  borderRadius: '6px',
  px: '10px',
  py: '6px',
  bgcolor: 'transparent',
  color: '#0F172A',
  fontSize: '0.8125rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  transition: 'background-color 120ms',
  '&:hover:not(:disabled)': { bgcolor: 'rgba(99,102,241,0.08)', color: '#4338CA' },
  '&:disabled': { cursor: 'default', color: '#94A3B8', '& kbd': { opacity: 0.5 } },
  '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: 0 },
} as const;

/**
 * The bulk actions row above the list. Its checkbox lines up with the row checkboxes and ticks
 * every card on screen; the actions apply to whatever is ticked. Claim and Unclaim carry the
 * number of cards they would change, because the API only claims unclaimed cards and the row
 * only releases the viewer's own claims.
 */
export default function BulkBar({
  count, total, claimable, releasable, busy, onToggleAll, onAction,
}: {
  count: number;
  total: number;
  claimable: number;
  releasable: number;
  busy: BulkAction | null;
  onToggleAll: () => void;
  onAction: (action: BulkAction) => void;
}) {
  const idle = busy === null;
  const none = count === 0;
  const all = count === total;
  const boxRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (boxRef.current) boxRef.current.indeterminate = count > 0 && !all; }, [count, all]);
  const label = (action: BulkAction, text: React.ReactNode) => (busy === action ? BUSY_LABEL[action] : text);
  return (
    <Box
      role="toolbar"
      aria-label="Bulk actions"
      sx={{
        position: 'sticky',
        // main scrolls under the fixed app bar (64px) and has 32px padding, which sticky adds to top.
        top: 40,
        zIndex: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '2px',
        pr: '6px',
        py: '5px',
        mb: '14px',
        bgcolor: none ? '#FFFFFF' : '#ECEDFE',
        border: `1px solid ${none ? '#E2E8F0' : '#C7CAFB'}`,
        borderRadius: '8px',
        transition: 'background-color 120ms, border-color 120ms',
      }}
    >
      {/* Same 40px cell as a row's checkbox column, so this box sits directly above the row boxes. */}
      <Box component="label" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', pr: '8px', py: '4px' }}>
        <Box sx={{ width: '40px', pl: '13px', display: 'flex', flexShrink: 0 }}>
          <Box
            component="input"
            type="checkbox"
            ref={boxRef}
            checked={all}
            disabled={!idle}
            onChange={onToggleAll}
            aria-label={all ? 'Clear selection' : `Select all ${total} cards`}
            sx={{ m: 0, width: 16, height: 16, cursor: 'pointer', accentColor: '#4F46E5', '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: 2 } }}
          />
        </Box>
        <Box component="span" role="status" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: none ? '#475569' : '#0F172A', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {none ? `Select all ${total}` : `${count} of ${total} selected`}
        </Box>
      </Box>
      <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', my: '6px', mx: '6px', bgcolor: none ? '#E2E8F0' : '#C7CAFB' }} />
      <Box component="button" type="button" disabled={!idle || claimable === 0} onClick={() => onAction('claim')} title={!none && claimable === 0 ? 'Every selected card is already claimed' : undefined} sx={barButton}>
        {label('claim', none ? 'Claim' : `Claim ${claimable}`)}<Kbd>C</Kbd>
      </Box>
      <Box component="button" type="button" disabled={!idle || releasable === 0} onClick={() => onAction('unclaim')} title={none ? undefined : releasable === 0 ? 'None of the selected cards is claimed by you' : 'Releases your claims only'} sx={barButton}>
        {label('unclaim', none ? 'Unclaim' : `Unclaim ${releasable}`)}
      </Box>
      <Box component="button" type="button" disabled={!idle || none} onClick={() => onAction('snooze')} sx={barButton}>
        {label('snooze', 'Snooze')}<Kbd>S</Kbd>
      </Box>
      <Box component="button" type="button" disabled={!idle || none} onClick={() => onAction('dismiss')} sx={barButton}>
        {label('dismiss', 'Dismiss')}<Kbd>X</Kbd>
      </Box>
    </Box>
  );
}
